import { Backdrop, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Modal, Select, TextField } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { MainContext } from '../../../../context/main/main-context';
import DataHandler from '../../../../handlers/DataHandler';
import { sweetStatusAlert } from '../../../../components/common/SweetAlert';

function AddPart({ isOpen, setIsOpen, currentPart, setCurrentPart, handlefetchAllParts }) {

    const { createPart, addPart, updatePart, handleUpdatePart, templateMetaData} = useContext(MainContext);

    const [loading, setLoading] = useState(false);
    const [newPart, setNewPart] = useState({
        name: currentPart?.name || '',
        description: currentPart?.description || '',
        status_id: currentPart?.status_id === 0? 0: 1,
        notation_id: currentPart?.notation_id || 2,
    });
    const [errors, setErrors] = useState({});

    const handleChange = (field) => (e) => {

        if (!e) return;

        let value = e.target ? e.target.value : e;
        
        setNewPart(prev => ({ ...prev, [field]: value }));

        if (field === "name") {
            const error = validatePartName(newPart.notation_id, value);
            setErrors((prev) => ({ ...prev, name: error }));
        }

        if (field === "notation_id") {
            const error = validatePartName(value, newPart.name);
            setErrors((prev) => ({ ...prev, name: error }));
        }

    };

    const handleCreate = async () => {
        try {
            setLoading(true);
            const user_id = DataHandler.getFromSession('manage_user_id');
            await createPart?.(newPart, user_id);
        } catch (error) {
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to create part", "error");
        }
    };

    const handleUpdate = async () => {
        try {
            setLoading(true);
            const user_id = DataHandler.getFromSession('manage_user_id');
            await handleUpdatePart?.(newPart, user_id, currentPart.part_id);
        } catch (error) {
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to update part", "error");
        }
    };

    const handleClose = () => {
            setNewPart({
                name: '',
                description: '',
                created_by: '',
                status_id: 1
            });
            setCurrentPart(null)
            setIsOpen(false);
    };

    const validatePartName = (notationId, value) => {
        if (!value) {
            return "Part Name is required.";
        }

        const nameExists = templateMetaData?.parts?.some(
            (part) => part.name.trim().toLowerCase() === value.trim().toLowerCase() && 
            part.part_id !== currentPart?.part_id 
        );

        if (nameExists) {
            return "Part name already exists.";
        }

        switch (notationId) {
            case 1: 
                if (!/^[A-Z]$/.test(value)) {
                    return "Enter a single uppercase letter (A-Z).";
                }
                break;

            case 2: 
                if (!/^[1-9]\d*$/.test(value)) {
                    return "Enter a positive integer (1,2,3,...).";
                }
                break;

            case 3: 
                if (!/^(I|II|III|IV|V|VI|VII|VIII|IX|X)$/.test(value.toUpperCase())) {
                    return "Enter valid Roman numeral (I to X).";
                }
                break;

            default:
                return null;
        }

        return null;
    };

    const isFormValid = () => {
        return !errors.name && newPart.name && newPart.notation_id;
    };

    useEffect(() => {
        if (addPart && addPart.success) {
            setLoading(true);
            sweetStatusAlert("Success", "Part created successfully", "success");
            handlefetchAllParts()
            handleClose();
        }
        else if(addPart && !addPart.success){
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to created part", "error");
        }
    }, [addPart]);

    useEffect(() => {
        if (updatePart && updatePart.success) {
            setLoading(true);
            sweetStatusAlert("Success", "Part updated successfully", "success");
            handlefetchAllParts()
            handleClose();
        }
        else if(updatePart && !updatePart.success){
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to update part", "error");
        }
    }, [updatePart]);

    useEffect(() => {
        const error = validatePartName(newPart.notation_id, newPart.name);
        setErrors({ name: error });
    }, []);

  return (
     <Modal 
                open={isOpen} 
                onClose={handleClose}
                disableEscapeKeyDown={loading} 
                sx={{
                    
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: -1 
                    }
                }}
            >
                <Box 
                   sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: 800,
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px',
                    zIndex: 1,
                    overflowY: 'auto',
                }}
                >
                
                    <Backdrop
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: (theme) => theme.zIndex.modal + 1,
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            borderRadius: '8px'
                        }}
                        open={loading}
                    >
                        <CircularProgress color="primary" />
                    </Backdrop>
    
                    <FormControl fullWidth sx={{ mb: 2 }}>

                       <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Part Notation</InputLabel>
                            <Select
                                value={newPart.notation_id || 2}
                                onChange={handleChange("notation_id")}
                                label="Part Notation"
                                disabled={loading}
                            >
                                {templateMetaData?.notations?.map((notation) => (
                                    <MenuItem key={notation.notation_id} value={notation.notation_id}>
                                        {notation.notation_type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>


                        <TextField
                            sx={{ mb: 3 }}
                            value={newPart.name}
                            onChange={handleChange("name")}
                            label="Part Name"
                            variant="outlined"
                            disabled={loading}
                            error={Boolean(errors.name)}
                            helperText={errors.name}
                        />
    
                        <TextField
                            sx={{ mb: 3 }}
                            value={newPart.description}
                            onChange={handleChange("description")}
                            label="Description"
                            variant="outlined"
                            disabled={loading}
                        />
    
    
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={newPart.status_id}
                                onChange={handleChange("status_id")}
                                label="Status"
                                disabled={loading}
                            >
                                {[{ value: "Active", key: 1 }, { value: "Inactive", key: 0 }].map(
                                    (option) => (
                                    <MenuItem key={option.key} value={option.key}>
                                        {option.value}
                                    </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                    </FormControl>
    
                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:2}}>
                        {currentPart && currentPart.part_id ? (
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={handleUpdate} 
                                disabled={!isFormValid() || loading}
                            >
                                Update
                            </Button>
                        ):(
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={handleCreate} 
                                disabled={!isFormValid() || loading}
                            >
                                Create
                            </Button>
                        )}
                        
                        <Button 
                            variant="outlined" 
                            color="secondary" 
                            onClick={handleClose} 
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </div>
                </Box>
            </Modal>
  )
}

export default AddPart